import * as productService from '@features/products/services/productService';
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";

export function useProducts() {
  const queryClient = useQueryClient();

  const { data: products, isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: () => productService.fetchProducts("isActive=true&limit=1000"),
  });

  const createMutation = useMutation({
    mutationFn: productService.createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: productService.updateProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products", "product"] });
    },
  });

  return {
    products,
    isLoading,
    createProduct: createMutation.mutate,
    updateProduct: updateMutation.mutate,
    createProductLoading: createMutation.isLoading,
    updateProductLoading: updateMutation.isLoading,
  };
}

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: productService.updateProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
  return {
    deleteProduct: deleteMutation.mutate,
    isLoading: deleteMutation.isPending,
  };
};

export const useProductsInfiniteQuery = (params) => {
  const searchParams = new URLSearchParams(params);
  return useInfiniteQuery({
    queryKey: ["products", params],
    queryFn: async ({ pageParam }) => {
      return productService.fetchProducts(
        `page=${pageParam}&${searchParams.toString()}`
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.nextPage !== null
        ? lastPage.pageInfo.nextPage
        : undefined;
    },
  });
};

export const useProductsSearchQuery = (
  search = "",
  limit = 50,
  page = 1,
  enabled = true
) => {
  const params = new URLSearchParams({
    name_q: search,
    isActive: "true",
    limit: limit.toString(),
    page: page.toString(),
  });

  return useQuery({
    queryKey: ["products-search", search, limit, page],
    queryFn: () => productService.fetchProducts(params.toString()),
    enabled: enabled && search.length >= 0, // Enable search even for empty strings to show all products
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });
};
